import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, X } from "lucide-react";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useToast } from "../hooks/use-toast";

const DUE = "lp-passkey-nudge-due";
const DONE = "lp-passkey-nudge-done";

export const markPasskeyNudgeDue = () => {
  try { if (!localStorage.getItem(DONE)) localStorage.setItem(DUE, "1"); } catch { /* ignore */ }
};

// Asked at the only moment a shop owner truly cares about their account: right after they've put
// a live deal into the world. Asked once, and never again once answered.
export const PasskeyNudge = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const { toast } = useToast();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DONE, "1"); localStorage.removeItem(DUE); } catch { /* ignore */ }
    setShow(false);
  }, []);

  useEffect(() => {
    if (user?.role !== "business" || !browserSupportsWebAuthn()) return;
    let due = false;
    try { due = !!localStorage.getItem(DUE) && !localStorage.getItem(DONE); } catch { return; }
    if (!due) return;
    api.get("/passkeys").then((r) => setShow(r.data.length === 0)).catch(() => setShow(false));
  }, [user]);

  const add = async () => {
    setBusy(true);
    try {
      const { data: start } = await api.post("/passkeys/register/options");
      const credential = await startRegistration({ optionsJSON: start.options });
      const label = /iphone|ipad|android/i.test(navigator.userAgent) ? "Phone" : "This computer";
      await api.post("/passkeys/register/finish", { state: start.state, credential, label });
      dismiss();
      toast({ title: t("pk.added"), description: t("pk.addedNote") });
    } catch (e) {
      const cancelled = e?.name === "NotAllowedError" || e?.name === "AbortError";
      toast({ title: cancelled ? t("pk.cancelled") : t("pk.failed"), description: cancelled ? undefined : e.response?.data?.detail });
    } finally { setBusy(false); }
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] left-4 right-4 sm:left-[17.5rem] sm:right-auto sm:max-w-sm z-[55] lp-fade-up" data-testid="passkey-nudge">
      <div className="bg-[#0f2a1a] text-white rounded-2xl shadow-2xl border border-white/10 p-4">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-full bg-[#16a34a]/20 flex items-center justify-center shrink-0">
            <KeyRound size={17} className="text-[#4ade80]" />
          </span>
          <div className="min-w-0">
            <p className="font-bold text-sm">{t("pn.title")}</p>
            <p className="text-xs text-emerald-100/80 mt-0.5">{t("pn.body")}</p>
          </div>
          <button onClick={dismiss} data-testid="passkey-nudge-dismiss" aria-label={t("pn.notNow")}
            className="ml-auto h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center shrink-0 transition-colors"><X size={14} /></button>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={add} disabled={busy} data-testid="passkey-nudge-add"
            className="flex-1 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-xs font-bold rounded-xl py-2.5 transition-colors inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={13} /> : <KeyRound size={13} />} {t("pn.add")}
          </button>
          <button onClick={dismiss} data-testid="passkey-nudge-later"
            className="text-xs font-semibold text-emerald-100/80 hover:text-white px-4 transition-colors">{t("pn.notNow")}</button>
        </div>
      </div>
    </div>
  );
};

export default PasskeyNudge;
